// Newsletter Subscription Service for Snapcipe AI Landing Page
//
// Security (#343): the browser NEVER talks to MailerLite directly and NEVER
// holds an API key. All signups are proxied through the Cloudflare Worker's
// /newsletter endpoint, which keeps the MailerLite key server-side and applies
// validation + rate limiting.

// Production Cloudflare Worker base URL. Hardcoded on purpose: the landing
// page is a static CRA build with no server-side env, and this URL is public
// anyway (it ships in the bundle either way). If the worker moves, update it
// here - this is the single source of truth for the landing page.
export const NEWSLETTER_API_BASE_URL = 'https://prod-mobile-ai-recipe-app.s4tdrctc6w.workers.dev';

class NewsletterService {
  constructor(baseUrl = NEWSLETTER_API_BASE_URL) {
    this.endpoint = `${baseUrl}/newsletter`;
  }

  /**
   * Validate email format (mirrors the server-side check for fast feedback)
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return typeof email === 'string' && email.length <= 254 && emailRegex.test(email.trim());
  }

  /**
   * Subscribe a user to the newsletter via the worker proxy
   * @param {string} email - User's email address
   * @returns {Promise<{success: boolean, message: string, isExisting?: boolean}>}
   */
  async subscribe(email) {
    if (!this.isValidEmail(email)) {
      throw new Error('Please enter a valid email address');
    }

    let response;
    try {
      response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
    } catch (error) {
      console.error('Newsletter subscription network error:', error);
      throw new Error('Network error. Please check your connection and try again.');
    }

    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      // Non-JSON response body - handled by status checks below
    }

    if (response.ok && data.ok) {
      if (data.alreadySubscribed) {
        return {
          success: true,
          message: "You're already subscribed to our newsletter!",
          isExisting: true,
        };
      }
      return {
        success: true,
        message: 'Successfully subscribed! Welcome to the Snapcipe AI community.',
      };
    }

    if (response.status === 400) {
      throw new Error('Please enter a valid email address');
    }
    if (response.status === 429) {
      throw new Error('Too many attempts. Please try again in a minute.');
    }
    if (response.status === 503) {
      throw new Error('Newsletter signup is temporarily unavailable. Please try again later.');
    }
    throw new Error(data.message || 'Failed to subscribe to newsletter');
  }
}

const newsletterService = new NewsletterService();
export default newsletterService;
